import React, {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";

import { useStorage } from "src/utils/storage";
import { useDispatch } from "react-redux";
import { resetJourney, setProfile } from "src/store/features/authSlice";
import { OPEN_ROUTES } from "src/config/constants";

interface AuthContextType {
  isAuthenticated: boolean;
  isTokenLoading: boolean;
  setUserProfile: (user: any) => void;
  clearStorage: () => void;
  tokens?: any;
  setTokens: (token: any) => void;
  logout: () => void;
  user?: any;
  optIn?: boolean | false;
  setOptIn: (optIn: boolean) => void;
}
export const AuthContext = createContext<AuthContextType>(
  {} as AuthContextType
);

export const AuthProvider = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const storage = useStorage();
  const defaultUser = {
    method: "signup",
    previousStep: "",
    token: "",
    role: "User",
    isProfileExist: false,
    links: OPEN_ROUTES,
    profileName: "",
    loyalty_points: 0,
    email: "",
  };
  const [user, setUser] = useState<any>({
    method: "signup",
    previousStep: "",
    token: "",
    role: "User",
    isProfileExist: false,
    links: OPEN_ROUTES,
    profileName: "",
    loyalty_points: 0,
    email: "",
  });
  const [isTokenLoading] = useState<boolean>(false);
  const [tokens, setAccessTokens] = useState<any>({});
  const [isOptIn, setIsOptIn] = useState<boolean>(false);
  const isAuthenticated = Boolean(tokens.accessToken || tokens.token);

  const setUserProfile = (data: any) => {
    storage.set("user_profile", JSON.stringify(data || defaultUser));
    setUser(data || defaultUser);
    dispatch(setProfile(data || defaultUser));
  };

  const setOptIn = (optIn: boolean) => {
    setIsOptIn(optIn);
  }

  const setTokens = (token: any) => {
    storage.set("app_tokens", JSON.stringify(token));
    setAccessTokens(token);
  };

  const delay = (ms: number) => {
    return new Promise((resolve: any) => setTimeout(resolve, ms));
  };
  const logout = async () => {
    await delay(500);
    await storage.clear();
    setUserProfile(null);
    setAccessTokens({});
    dispatch(resetJourney());
  };

  const clearStorage = async () => {
    await storage.clear();
  };

  useImperativeHandle(ref, () => ({
    async logout() {
      await logout();
    },
  }));

  const hydrateAuthFromStorage = useCallback(async () => {
    try {
      const storedTokens = await storage.get("app_tokens");
      if (storedTokens) {
        try {
          const parsedTokens = JSON.parse(storedTokens);
          if (parsedTokens && typeof parsedTokens === "object") {
            setAccessTokens(parsedTokens);
          }
        } catch (error) {
          console.error("Failed to parse stored tokens", error);
        }
      }

      const storedProfile = await storage.get("user_profile");
      if (storedProfile) {
        try {
          const parsedProfile = JSON.parse(storedProfile);
          if (parsedProfile) {
            setUser(parsedProfile);
            dispatch(setProfile(parsedProfile));
          }
        } catch (error) {
          console.error("Failed to parse stored user profile", error);
        }
      }
    } catch (error) {
      console.error("Failed to hydrate auth context from storage", error);
    }
  }, [dispatch, storage]);

  useEffect(() => {
    if (tokens.accessToken === "") {
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokens.accessToken]);

  useEffect(() => {
    hydrateAuthFromStorage();
  }, [hydrateAuthFromStorage]);

  return (
    <AuthContext.Provider
      value={{
        setTokens,
        logout,
        clearStorage,
        user,
        isTokenLoading,
        tokens,
        setUserProfile,
        isAuthenticated,
        optIn: isOptIn,
        setOptIn,
      }}
    >
      {(props as any).children}
    </AuthContext.Provider>
  );
});
export default function useAuth() {
  return useContext(AuthContext);
}
